/**
 * Hook React Query pour les notifications
 * Avec support Realtime optionnel
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { isFeatureEnabled } from '@/config/features'
import { useSecureRealtime } from '@/hooks/useSecureRealtime'
import { healthMonitor } from '@/lib/healthCheck'

interface ActivityNotification {
  id: string
  type: string
  message: string
  is_read: boolean
  created_at: string | null
  campaign_id: string | null
  referent_id: string | null
}

/**
 * Hook pour charger les notifications avec React Query + Realtime
 */
export function useNotificationsQuery() {
  const { user } = useAuth()
  
  // ✅ Stabiliser queryKey avec useMemo pour éviter remounts
  const queryKey = useMemo(
    () => ['notifications', user?.id ?? ''] as const,
    [user?.id]
  )
  
  // Query React Query avec fallback polling
  const query = useQuery({
    queryKey,
    
    queryFn: async (): Promise<ActivityNotification[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      // Enregistrer le render pour monitoring
      healthMonitor.recordRender()

      const { data, error } = await supabase
        .from('activity_notifications')
        .select('*')
        .eq('recruiter_id', user.id)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) throw error
      return data || []
    },
    
    enabled: !!user?.id,
    
    // Refetch automatique (contrôlé par feature flag)
    refetchOnWindowFocus: isFeatureEnabled('REACT_QUERY_REFETCH', user?.id),
    refetchOnReconnect: isFeatureEnabled('REACT_QUERY_REFETCH', user?.id),
    
    // Polling fallback plus fréquent pour les notifications
    refetchInterval: 15000, // 15 secondes
    
    // Cache plus court pour les notifications
    staleTime: 1000 * 15, // 15 secondes
  })
  
  // Realtime optionnel (avec garde-fous)
  // ✅ Stabiliser realtimeFilter avec useMemo pour éviter remounts
  const realtimeFilter = useMemo(
    () => `recruiter_id=eq.${user?.id}`,
    [user?.id]
  )
  
  useSecureRealtime({
    queryKey,
    table: 'activity_notifications',
    filter: realtimeFilter,
    userId: user?.id,
    enabled: !!user?.id,
  })
  
  return query
}
